"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';

const questions = ['What should we stop?', 'What should we fix?', 'What should we scale?', 'Where should we invest next?'];

function DecisionTree() {
    return (
        <div className="relative w-full flex flex-col items-center">
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="rounded-full px-6 py-3 text-sm font-bold"
                style={{ fontFamily: CLASH, background: PURPLE, color: '#ffffff' }}
            >
                Your Data
            </motion.div>

            <motion.div
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="w-px h-8"
                style={{ background: 'rgba(55,48,163,0.35)', transformOrigin: 'top' }}
            />

            <div className="relative w-full max-w-[820px]">
                <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="absolute top-0 h-px"
                    style={{ left: '12.5%', right: '12.5%', background: 'rgba(55,48,163,0.35)' }}
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 pt-0">
                    {questions.map((q, i) => (
                        <div key={q} className="flex flex-col items-center">
                            <motion.div
                                initial={{ scaleY: 0 }}
                                whileInView={{ scaleY: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: 0.55 + i * 0.06 }}
                                className="w-px h-8"
                                style={{ background: 'rgba(55,48,163,0.35)', transformOrigin: 'top' }}
                            />
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.65 + i * 0.08, ease: 'easeOut' }}
                                className="w-full rounded-2xl px-4 py-5 text-center"
                                style={{ background: 'rgba(55,48,163,0.05)', border: '1px solid rgba(55,48,163,0.18)' }}
                            >
                                <span className="text-sm md:text-base font-bold leading-snug" style={{ fontFamily: CLASH, color: INK }}>
                                    {q}
                                </span>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function AnalyticsDecisionMaking() {
    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1000px] mx-auto relative z-10 flex flex-col gap-14">
                <div className="flex flex-col gap-6 max-w-[760px]">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        Not Just a Dashboard
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        From Reporting to Decision-Making
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="text-base md:text-lg leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                    >
                        A dashboard shouldn&apos;t exist just because someone asked for a dashboard. The purpose of measurement is to make better decisions:
                    </motion.p>
                </div>

                <DecisionTree />

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="text-base md:text-lg leading-relaxed text-center"
                    style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                >
                    We build analytics around those questions.
                </motion.p>
            </div>
        </section>
    );
}
